import {
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline'
import { DocumentDocument } from '../../lib/db/db.types'
import { ENABLE_DOCUMENT_SIGNING } from '../../config/config'

type DocumentListItemProps = {
  id: string
  data: DocumentDocument
  dropdownOpen: string | null
  onToggleDropdown: (id: string | null) => void
  onSetFinancialYear: (id: string, name: string, currentYear?: string) => void
  onDelete: (id: string, storageRef: string, fileName: string) => void
  onDownload: (storageRef: string) => void
  accountingReferenceDate?: string
  isMultiSelectMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  onSignDocument?: (id: string) => void
}

export const DocumentListItem = ({
  id,
  data,
  dropdownOpen,
  onToggleDropdown,
  onSetFinancialYear,
  onDelete,
  onDownload,
  accountingReferenceDate,
  isMultiSelectMode,
  isSelected,
  onToggleSelect,
  onSignDocument,
}: DocumentListItemProps) => {
  return (
    <div className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white flex flex-col justify-between">
      <div>
        <div className="flex justify-between items-start mb-2">
          <p className="font-medium truncate flex-1">{data.name}</p>
          <div className="relative">
            <button
              onClick={() => onToggleDropdown(dropdownOpen === id ? null : id)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
            </button>
            {dropdownOpen === id && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border">
                <div className="py-1">
                  {ENABLE_DOCUMENT_SIGNING && (
                    <button
                      onClick={() => {
                        onSignDocument?.(id)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign document
                    </button>
                  )}
                  <button
                    onClick={() =>
                      onSetFinancialYear(id, data.name, accountingReferenceDate)
                    }
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Set Financial Year
                  </button>
                  <button
                    onClick={() => onDelete(id, data.storageRef, data.name)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Uploaded: {data.metadata.uploadedAt.toDate().toLocaleDateString()}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {(data.metadata.size / 1024).toFixed(2)} KB
        </p>
      </div>
      <div className="flex items-center justify-between mt-4 pt-2 border-t">
        <div className="flex items-center gap-2">
          {isMultiSelectMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="h-4 w-4 border-gray-300"
            />
          )}
          <div
            onClick={() => onSignDocument?.(id)}
            className="cursor-pointer text-gray-600 hover:text-blue-600"
          >
            {data.signing?.status === 'signed' ? (
              <span className="text-sm">✓ Signed</span>
            ) : data.signing?.status === 'signing-in-progress' ||
              data.signing?.status === 'signing-complete' ? (
              <span className="text-sm">Signing in progress</span>
            ) : data.signing?.status === 'setup' ? (
              <span className="text-sm">Preparing for signing</span>
            ) : null}
          </div>
        </div>
        <button
          onClick={() =>
            onDownload(
              data.signing?.signedDocumentStorageRef ?? data.storageRef,
            )
          }
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span>Download</span>
        </button>
      </div>
    </div>
  )
}
